import React from 'react';
import './FisaCoolingOffClosure.scss';
import Button from '../../../Form/Button/Button';
import ThumbsUpIcon from '../../../../assets/images/Thumbs Up.svg';
import WarningIcon from '../../../../assets/images/warning-outline.svg';
import {useUnifiedAnalytics} from '../../../../contexts/AnalyticsWrapper';
import {useLogger} from '../../../../contexts/Logger';

/** @import { FisaCoolingOffClosureStepTwoProps } from './FisaAccountClosureTypes' */

/**
 * FISA Cooling Off Closure Step Two Component
 * @param {FisaCoolingOffClosureStepTwoProps} props
 */
function FisaCoolingOffClosureStepTwo({ finalPaymentAmount, interestAccrued, payoutDestination, showLinkedAccountPayoutWarning, onCloseAccount, onCancel }) {
	const analytics = useUnifiedAnalytics();
	const logger = useLogger();

	/**
	 * Handle close account click with analytics tracking
	 */
	const handleCloseAccountClick = () => {

		logger.info('User confirmed FISA cooling off account closure', {
			additionalData: {
				finalPaymentAmount: finalPaymentAmount,
				interestAccrued: interestAccrued
			}
		});

		analytics.trackAnalyticsClickEvent(
			'fisa cooling off final close account',
			window.location.href,
			'fisa-closure-step2',
			'fisa_1yr',
			'',
			{},
			'button click',
			'close_fisa_account|button_click|close_account_final',
			'account_closure_cooling_off'
		);
		onCloseAccount();
	};

	/**
	 * Calculate and format the total payment amount including interest
	 * @returns {string} Formatted payment amount
	 */
	const formatTotalPayment = () => {
		const baseAmount = Number(finalPaymentAmount.toString().replace(/[£,]/g, '')) || 0;
		const interest = (interestAccrued !== undefined && interestAccrued !== null) ? Number(interestAccrued) : 0;
		const totalAmount = baseAmount + interest;
		return `£${totalAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
	};

	const consequences = payoutDestination === 'instant-access-cash-isa'
		? [
			'Your balance will be paid to your Instant Access Cash ISA and any interest accrued will be paid to your linked account.',
			'Your final balance and interest earned so far will remain tax-free.',
			'You can open another Fixed Rate Cash ISA with us in the future, if you choose to.',
			'You’ll still have access to your online dashboard after your account is closed, where you can view or download your statements and interest certificates.'
		]
		: [
			'Your interest earned so far will remain tax-free, and you’ll still benefit from your ISA allowance for this tax year.',
			'You can open another Fixed Rate Cash ISA with us in the future, if you choose to.',
			'Your final balance, including any interest, will be paid into your linked account.',
			'You’ll still have access to your online dashboard after your account is closed, where you can view or download your statements and interest certificates.'
		];

	return (
		<div className="fisaCoolingOff__content">
			<h2 className="fisaCoolingOff__sectionTitle">Important information</h2>
			<div className="fisaCoolingOff__subtitle">If you choose to go ahead:</div>
			<div className="fisaCoolingOff__consequences">
				<div className="fisaCoolingOff__consequenceList">
					{consequences.map(consequence => (
						<div className="fisaCoolingOff__consequenceItem" key={consequence}>
							<div className="fisaCoolingOff__consequenceIcon">
								<img src={ThumbsUpIcon} alt="Thumbs up" width="24" height="24" />
							</div>
							<div className="fisaCoolingOff__consequenceText">{consequence}</div>
						</div>
					))}
				</div>
			</div>
			<div className="fisaCoolingOff__paymentBox">
				<div className="fisaCoolingOff__paymentLabel">Final payment amount including interest</div>
				<div className="fisaCoolingOff__paymentAmount">{formatTotalPayment()}</div>
			</div>
			{showLinkedAccountPayoutWarning && (
				<div className="fisaCoolingOff__warningBox" role="alert">
					<img src={WarningIcon} alt="" />
					<span>If you have moved funds from an ISA to this account, paying funds to your linked account means they will lose their tax-free status.</span>
				</div>
			)}
			<div className="fisaCoolingOff__instruction">
				When you're ready, select 'Close my account' to confirm.
			</div>
			<div className="fisaCoolingOff__buttonRow">
				<Button
					size="med"
					label="Close my account"
					onClick={handleCloseAccountClick}
				/>
				<Button
					variant="secondary"
					size="med"
					label="Don't close my account"
					onClick={onCancel}
				/>
			</div>
		</div>
	);
}

export default FisaCoolingOffClosureStepTwo;
